<template>
  <div v-if="user" class="w-[95%] mx-auto">
    <div v-if="banner" class="mb-3 px-4 py-2 rounded border text-sm"
         :class="banner.type === 'ok' ? 'border-green-500 text-green-500' : 'border-red-500 text-red-500'">
      {{ banner.text }}
    </div>

    <!-- the one update -->
    <v-card class="bg-white text-black p-4 border-t">
      <div class="flex justify-between items-start flex-wrap gap-3">
        <div>
          <div class="text-[16px] font-semibold">Update all apps</div>
          <div class="text-xs text-gray-500">Backend, sync worker and dashboard move together to the latest <b>production</b> branch. If any of them fails its health check, the ones already updated are put back. One rollback undoes the whole update.</div>
        </div>
        <v-chip size="small" :color="busy ? 'warning' : 'success'" variant="tonal">{{ busy ? busyText : 'idle' }}</v-chip>
      </div>

      <div class="flex gap-3 items-center flex-wrap mt-4">
        <span class="text-sm">Branch: <span class="font-mono font-semibold">{{ RELEASE_REF }}</span></span>
        <v-btn size="small" color="primary" variant="tonal" :disabled="planning" @click="checkPlan()">⟳ Check for new changes</v-btn>
      </div>

      <div v-if="planning" class="text-gray-500 mt-3">Checking all three repos…</div>
      <template v-else-if="plan">
        <v-table density="compact" class="bg-white text-black mt-3">
          <thead><tr><th>App</th><th>Running</th><th></th><th>Would run</th><th>Change</th></tr></thead>
          <tbody>
            <tr v-for="(a, key) in plan.apps" :key="key" :class="a.changed ? 'row-change' : ''">
              <td class="font-semibold">{{ key }}</td>
              <td class="font-mono">{{ a.short || '?' }}</td>
              <td>{{ a.found ? '→' : '' }}</td>
              <td class="font-mono">{{ a.found ? a.target_short : '' }}</td>
              <td>
                <span v-if="!a.found" class="text-red-500">{{ a.error || ('no "' + plan.ref + '" in this repo') }}</span>
                <span v-else-if="!a.changed" class="text-gray-500">unchanged</span>
                <span v-else>{{ a.target_subject }} <span class="text-gray-500">· {{ a.target_author }} · {{ a.target_date }}</span></span>
              </td>
            </tr>
          </tbody>
        </v-table>
        <div class="flex gap-2 items-center mt-4 flex-wrap">
          <v-btn color="primary" variant="flat" :disabled="!plan.ok || !plan.changes.length || busy" @click="confirm('release')">
            Update {{ plan.changes.length ? plan.changes.join(' + ') : 'nothing' }} to {{ plan.ref }}
          </v-btn>
          <span v-if="!plan.ok" class="text-sm text-red-500">"{{ plan.ref }}" must exist in all three repos before it can be released.</span>
          <span v-else-if="!plan.changes.length" class="text-sm text-gray-500">Everything already runs {{ plan.ref }}.</span>
          <span v-else class="text-sm text-gray-500">Backend restarts in ~3 s; dashboard rebuild takes ~1 min with no downtime; sync restart is invisible to customers.</span>
        </div>
      </template>
    </v-card>

    <!-- rollback -->
    <v-card class="bg-white text-black p-4 mt-4 border-t">
      <div class="flex justify-between items-center flex-wrap gap-3">
        <div>
          <div class="text-[16px] font-semibold">Roll back the last update</div>
          <div v-if="lastRelease" class="text-xs text-gray-500">
            Last update: <b>{{ lastRelease.ref }}</b> by {{ lastRelease.who }} at {{ lastRelease.ts.replace('T', ' ').slice(0, 16) }} UTC, changed
            <span v-for="a in lastRelease.changed" :key="a" class="font-mono">{{ a }} {{ lastRelease.before[a].slice(0, 9) }}→{{ lastRelease.after[a].slice(0, 9) }}; </span>
            Rolling back puts each of them back to the commit on the left, instantly where the previous build is still on the server.
          </div>
          <div v-else class="text-xs text-gray-500">Nothing to roll back: no update has been made from here yet, or the last one was already rolled back.</div>
        </div>
        <v-btn color="warning" variant="tonal" :disabled="!lastRelease || busy" @click="confirm('rollback')">↩ Roll back all</v-btn>
      </div>
    </v-card>

    <!-- what runs now -->
    <div class="grid md:grid-cols-3 grid-cols-1 gap-4 mt-4">
      <v-card v-for="(s, key) in apps" :key="key" class="bg-white text-black p-4 border-t">
        <div class="flex justify-between items-start">
          <div><div class="font-semibold">{{ s.label }}</div>
            <div class="text-xs text-gray-500">{{ key }} · {{ s.host || '?' }} · <a :href="'https://github.com/' + s.repo" target="_blank" class="underline">{{ s.repo }}</a></div></div>
          <v-chip size="small" :color="chipColor(s)" variant="tonal">{{ chipText(s) }}</v-chip>
        </div>
        <div v-if="s.error" class="mt-3 text-red-500 text-sm">Unreachable: {{ s.error }}</div>
        <template v-else>
          <div class="font-mono text-[18px] font-bold mt-2">{{ s.short }}</div>
          <div class="text-xs text-gray-500">{{ s.subject }} · {{ s.author }} · {{ s.date }}<span v-if="s.branches"> · on {{ s.branches }}</span></div>
          <div class="text-xs text-gray-500 mt-1">previous build on server: <span class="font-mono">{{ s.prev_short || 'none' }}</span></div>
        </template>
      </v-card>
    </div>

    <!-- live output -->
    <v-card class="bg-white text-black p-4 mt-4 border-t">
      <div class="font-semibold">Live output
        <span v-if="job" class="font-mono text-sm font-normal text-gray-500">— {{ job.action }} {{ job.ref }} ({{ job.status }}, by {{ job.who }})</span>
      </div>
      <pre ref="consoleEl" class="mt-2 bg-gray-900 text-gray-100 rounded p-3 text-xs overflow-auto" style="max-height:360px;white-space:pre-wrap;word-break:break-all">{{ job && job.lines.length ? job.lines.join('\n') : 'No update running.' }}</pre>
    </v-card>

    <!-- recent changes: every update made from here, newest first, each restorable -->
    <v-card class="bg-white text-black p-4 mt-4 border-t">
      <div class="font-semibold">Recent changes</div>
      <div class="text-xs text-gray-500 mb-2">The last 10 updates made from this page. Each line lists the code changes it shipped. Restore puts backend, sync and frontend back to exactly what was running right after that update.</div>
      <v-table density="compact" class="bg-white text-black">
        <thead><tr><th>When (UTC)</th><th>Who</th><th>Update</th><th>Changes shipped</th><th></th></tr></thead>
        <tbody>
          <tr v-for="r in (release?.history || [])" :key="r.id" :class="isCurrent(r) ? 'row-current' : ''">
            <td class="align-top whitespace-nowrap">{{ r.ts.replace('T', ' ').slice(0, 16) }}</td>
            <td class="align-top">{{ r.who }}</td>
            <td class="align-top">
              <div class="font-mono">{{ r.ref }}</div>
              <v-chip size="x-small" :color="r.status === 'ok' ? 'success' : 'error'" variant="tonal">{{ r.status }}</v-chip>
              <v-chip v-if="isCurrent(r)" size="x-small" color="success" class="ml-1">running now</v-chip>
            </td>
            <td class="align-top" style="white-space:normal;min-width:360px">
              <div v-if="!r.changed.length" class="text-gray-500">nothing changed</div>
              <div v-for="a in r.changed" :key="a" class="mb-1">
                <span class="font-semibold">{{ a }}</span>
                <span class="font-mono text-xs text-gray-500"> {{ r.before[a].slice(0, 9) }} → {{ r.after[a].slice(0, 9) }}</span>
                <ul class="ml-4 text-sm" style="list-style:disc">
                  <li v-for="c in (r.summary?.[a] || [])" :key="c.short">
                    <span :class="c.reverted ? 'line-through text-gray-500' : ''">{{ c.subject }}</span>
                    <span class="text-xs text-gray-500"> · {{ c.short }} · {{ c.author }} · {{ c.date }}{{ c.reverted ? ' (undone)' : '' }}</span>
                  </li>
                  <li v-if="!(r.summary?.[a] || []).length" class="text-gray-500 text-sm">no commit details recorded</li>
                </ul>
              </div>
            </td>
            <td class="align-top">
              <v-btn v-if="r.status === 'ok'" size="x-small" color="warning" variant="tonal" :disabled="isCurrent(r) || busy" @click="confirm('restore', r)">↩ Restore</v-btn>
            </td>
          </tr>
          <tr v-if="!(release?.history || []).length"><td colspan="5" class="text-gray-500">No updates made from here yet.</td></tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- confirm -->
    <v-dialog v-model="dlg.open" max-width="560">
      <v-card class="bg-white text-black p-4">
        <div class="text-[16px] font-semibold mb-2">{{ dlg.action === 'release' ? 'Update all apps to ' + plan?.ref + '?' : dlg.action === 'restore' ? 'Restore the update from ' + (dlg.rec?.ts || '').replace('T', ' ').slice(0, 16) + ' UTC?' : 'Roll back the last update?' }}</div>
        <div v-if="dlg.action === 'release'" class="mb-2 text-sm">
          <div v-for="a in plan?.changes || []" :key="a" class="font-mono">{{ a }}: {{ plan.apps[a].short }} → {{ plan.apps[a].target_short }}</div>
        </div>
        <div v-else-if="dlg.action === 'restore'" class="mb-2 text-sm">
          <div v-for="(sha, a) in dlg.rec?.after || {}" :key="a" class="font-mono">{{ a }}: {{ apps[a]?.short || '?' }} → {{ sha.slice(0, 9) }}<span v-if="apps[a]?.sha === sha" class="text-gray-500"> (already there)</span></div>
        </div>
        <div v-else class="mb-2 text-sm">
          <div v-for="a in lastRelease?.changed || []" :key="a" class="font-mono">{{ a }}: {{ lastRelease.after[a].slice(0, 9) }} → {{ lastRelease.before[a].slice(0, 9) }}</div>
        </div>
        <div class="text-sm text-gray-500 mb-4">Backend restart ≈ 3 s of API downtime; dashboard switch has none; sync restart is invisible to customers. If any app fails its health check, everything is put back automatically.</div>
        <div class="flex justify-end gap-2">
          <v-btn color="primary" variant="text" @click="dlg.open = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="runAction">Yes, do it</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useAdminDeployStore, useAccountStore, useAlertStore } from '@/stores';

const deployStore = useAdminDeployStore();
const accountStore = useAccountStore();
const alertStore = useAlertStore();
const { user } = storeToRefs(accountStore);
const { apps, release, plan, planning } = storeToRefs(deployStore);

// releases always go out from the production branch; nothing else can be picked
const RELEASE_REF = 'production';
const job = ref(null);
const banner = ref(null);
const consoleEl = ref(null);
const dlg = reactive({ open: false, action: '', rec: null });
const isCurrent = (r) => r.status === 'ok' && Object.keys(r.after || {}).length > 0 &&
  Object.entries(r.after).every(([a, sha]) => apps.value[a]?.sha === sha);
let pollTimer = null, stateTimer = null, activityTimer = null, watching = false;

// updates running anywhere: a release or a single app, from this page or the command line
const running = ref([]);
const busy = computed(() => running.value.length > 0 || job.value?.status === 'running' || release.value?.busy === 'yes' || release.value?.job?.status === 'running');
const busyText = computed(() => {
  const names = running.value.map((j) => j.app === 'release' ? 'all apps' : j.app);
  return names.length ? 'updating · ' + names.join(', ') : 'updating';
});
async function checkActivity() {
  let now;
  try { now = await deployStore.activity(); } catch (e) { return; }   // backend restarting: keep what is shown
  const was = running.value.length > 0;
  running.value = now;
  // something just finished: show the new commits, history and plan
  if (was && !now.length && !watching) { await deployStore.loadState(); checkPlan(); }
}
const lastRelease = computed(() => release.value?.last || null);
const chipColor = (s) => s.error ? 'error' : (s.pm2 ? (s.pm2_status === 'online' ? 'success' : 'error') : 'success');
const chipText = (s) => s.error ? 'offline' : (s.pm2 ? 'pm2 ' + s.pm2_status : 'static · nginx') + (s.dirty > 0 ? ' · modified' : '');

async function checkPlan() {
  banner.value = null;
  await deployStore.loadPlan(RELEASE_REF);   // the plan fetches from GitHub first
}

function confirm(action, rec = null) { dlg.action = action; dlg.rec = rec; dlg.open = true; }

async function runAction() {
  dlg.open = false; banner.value = null;
  try {
    job.value = dlg.action === 'release' ? await deployStore.startRelease(plan.value.ref)
              : dlg.action === 'restore' ? await deployStore.startRestore(dlg.rec.id)
              : await deployStore.startRollback();
    watch();
  } catch (e) {
    alertStore.error(typeof e === 'string' ? e : (e?.message || 'Could not start'));
  }
}

function watch() {
  watching = true; clearTimeout(pollTimer);
  const tick = async () => {
    try {
      const j = await deployStore.job();
      if (j) { job.value = j; await nextTick(); if (consoleEl.value) consoleEl.value.scrollTop = consoleEl.value.scrollHeight; }
      if (!j || j.status === 'running') { pollTimer = setTimeout(tick, 2000); return; }
      watching = false;
      const r = j.result || {};
      if (j.status === 'ok') banner.value = { type: 'ok', text: j.action === 'release' ? `Updated to ${j.ref}: ${r.changed === 'none' ? 'nothing needed changing' : r.changed.replace(/,/g, ', ')}.` : j.action === 'release-restore' ? `Restored: ${(r.changed || 'none').replace(/,/g, ', ')}.` : `Rolled back: ${(r.changed || '').replace(/,/g, ', ')}.` };
      else banner.value = { type: 'err', text: `${j.action} failed (${r.reason || 'see output'}). Apps that had been updated were put back.` };
      deployStore.loadState();
      if (j.action === 'release' && j.status === 'ok') checkPlan();
    } catch (e) {
      // the backend restarts itself mid-release: keep polling
      pollTimer = setTimeout(tick, 3000);
    }
  };
  tick();
}

onMounted(async () => {
  await deployStore.loadState();
  if (release.value?.job?.status === 'running') watch();
  checkPlan();
  stateTimer = setInterval(() => { if (!watching) deployStore.loadState(); }, 30000);
  checkActivity();
  activityTimer = setInterval(checkActivity, 3000);
});
onBeforeUnmount(() => { clearTimeout(pollTimer); clearInterval(stateTimer); clearInterval(activityTimer); });
</script>

<style scoped>
.row-change td { background: rgba(79, 140, 255, 0.14); }
.row-current td { background: rgba(62, 207, 142, 0.12); }
</style>
